import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../api';
import '../../Marketplace/Marketplace.css';

const STATUS_COLORS = {
  ACTIVE: { bg: '#d1fae5', text: '#065f46' },
  SUSPENDED: { bg: '#fee2e2', text: '#991b1b' },
  PENDING: { bg: '#fef3c7', text: '#92400e' },
};
const TYPE_COLORS = {
  CISO_RESELLER: { bg: '#dbeafe', text: '#1e40af' },
  CPA_FIRM: { bg: '#ede9fe', text: '#5b21b6' },
};

export default function PartnersPage() {
  const { t } = useTranslation();
  const PARTNER_TYPE_LABELS = { CISO_RESELLER: t('PartnersPage.partnerTypeCisoReseller'), CPA_FIRM: t('PartnersPage.partnerTypeCpaFirm') };
  const [partners, setPartners]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [msg, setMsg]             = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm]           = useState({
    partnerType: 'CISO_RESELLER', firmName: '', contactEmail: '',
    websiteUrl: '', commissionRate: '20',
  });
  const [creating, setCreating]   = useState(false);
  const [search, setSearch]       = useState('');

  const fetchPartners = useCallback(() => {
    setLoading(true);
    axiosInstance.get('/partner/admin/all')
      .then(res => setPartners(res.data))
      .catch(() => setMsg(t('PartnersPage.errorLoadPartners')))
      .finally(() => setLoading(false));
  }, [t]);

  useEffect(() => { fetchPartners(); }, [fetchPartners]);

  const handleCreate = async () => {
    if (!form.firmName) { setMsg(t('PartnersPage.errorFirmNameRequired')); return; }
    setCreating(true); setMsg('');
    try {
      await axiosInstance.post('/partner/admin/create', {
        ...form, commissionRate: parseFloat(form.commissionRate) || 20,
      });
      setShowCreate(false);
      setForm({ partnerType: 'CISO_RESELLER', firmName: '', contactEmail: '', websiteUrl: '', commissionRate: '20' });
      fetchPartners();
      setMsg(t('PartnersPage.successPartnerCreated'));
    } catch { setMsg(t('PartnersPage.errorCreatePartner')); }
    finally { setCreating(false); }
  };

  const handleStatusChange = async (partnerId, status) => {
    try {
      await axiosInstance.put(`/partner/admin/${partnerId}/status`, { status });
      fetchPartners();
      setMsg(t('PartnersPage.successStatusUpdated', { status }));
    } catch { setMsg(t('PartnersPage.errorUpdateStatus')); }
  };

  const filtered = partners.filter(p =>
    p.firmName?.toLowerCase().includes(search.toLowerCase()) ||
    p.partnerType?.toLowerCase().includes(search.toLowerCase()) ||
    p.contactEmail?.toLowerCase().includes(search.toLowerCase()));

  const cisoCount = partners.filter(p => p.partnerType === 'CISO_RESELLER').length;
  const cpaCount  = partners.filter(p => p.partnerType === 'CPA_FIRM').length;
  const activeCount = partners.filter(p => p.status === 'ACTIVE').length;

  return (
    <div className="mp-wrap">
      <div className="mp-header">
        <div>
          <h2 className="mp-title"><i className="fas fa-network-wired" /> {t('PartnersPage.title')}</h2>
          <p className="mp-subtitle">
            {t('PartnersPage.subtitleCisoCount', { count: cisoCount })} · {t('PartnersPage.subtitleCpaCount', { count: cpaCount })} · {t('PartnersPage.subtitleActiveCount', { count: activeCount })}
          </p>
        </div>
        <button className="mp-btn-primary" onClick={() => { setShowCreate(true); setMsg(''); }}>
          <i className="fas fa-plus" /> {t('PartnersPage.addPartner')}
        </button>
      </div>

      {msg && <div className="mp-msg">{msg}</div>}

      {/* KPI row */}
      <div className="mp-kpi-row">
        <div className="mp-kpi">
          <div className="mp-kpi-label">{t('PartnersPage.kpiTotalPartners')}</div>
          <div className="mp-kpi-value">{partners.length}</div>
        </div>
        <div className="mp-kpi">
          <div className="mp-kpi-label">{t('PartnersPage.kpiCisoResellers')}</div>
          <div className="mp-kpi-value" style={{ color: '#1e40af' }}>{cisoCount}</div>
        </div>
        <div className="mp-kpi">
          <div className="mp-kpi-label">{t('PartnersPage.kpiCpaFirms')}</div>
          <div className="mp-kpi-value" style={{ color: '#5b21b6' }}>{cpaCount}</div>
        </div>
        <div className="mp-kpi">
          <div className="mp-kpi-label">{t('PartnersPage.kpiActive')}</div>
          <div className="mp-kpi-value" style={{ color: '#065f46' }}>{activeCount}</div>
        </div>
      </div>

      <div className="mp-section">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <i className="fas fa-search" style={{ position: 'absolute', left: 10, top: '50%',
              transform: 'translateY(-50%)', color: '#94a3b8', fontSize: 13 }} />
            <input className="mp-input" style={{ paddingLeft: 32, width: '100%' }}
              placeholder={t('PartnersPage.searchPlaceholder')} value={search}
              onChange={e => setSearch(e.target.value)} />
          </div>
        </div>

        {loading ? <div className="mp-loading"><i className="fas fa-spinner fa-spin" /> {t('PartnersPage.loading')}</div> : (
          filtered.length === 0 ? (
            <div className="mp-empty">{t('PartnersPage.emptyNoPartners')}</div>
          ) : (
            <table className="mp-table">
              <thead>
                <tr><th>{t('PartnersPage.thFirm')}</th><th>{t('PartnersPage.thType')}</th><th>{t('PartnersPage.thContact')}</th><th>{t('PartnersPage.thCommission')}</th><th>{t('PartnersPage.thStatus')}</th><th>{t('PartnersPage.thCreated')}</th><th>{t('PartnersPage.thActions')}</th></tr>
              </thead>
              <tbody>
                {filtered.map(p => {
                  const sc = STATUS_COLORS[p.status] || STATUS_COLORS.PENDING;
                  const tc = TYPE_COLORS[p.partnerType] || { bg: '#f1f5f9', text: '#475569' };
                  return (
                    <tr key={p.id}>
                      <td>
                        <div style={{ fontWeight: 600 }}>{p.firmName}</div>
                        {p.websiteUrl && <div style={{ fontSize: 11, color: '#94a3b8' }}>{p.websiteUrl}</div>}
                      </td>
                      <td>
                        <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700,
                          background: tc.bg, color: tc.text }}>
                          {PARTNER_TYPE_LABELS[p.partnerType] || p.partnerType}
                        </span>
                      </td>
                      <td>{p.contactEmail || '—'}</td>
                      <td style={{ fontWeight: 600 }}>{p.commissionRate}%</td>
                      <td>
                        <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700,
                          background: sc.bg, color: sc.text }}>
                          {p.status}
                        </span>
                      </td>
                      <td>{p.createdAt ? new Date(p.createdAt).toLocaleDateString() : '—'}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          {p.status === 'ACTIVE' ? (
                            <button className="mp-btn-sm mp-btn-danger"
                              onClick={() => handleStatusChange(p.id, 'SUSPENDED')}>
                              {t('PartnersPage.actionSuspend')}
                            </button>
                          ) : (
                            <button className="mp-btn-sm"
                              onClick={() => handleStatusChange(p.id, 'ACTIVE')}>
                              {t('PartnersPage.actionActivate')}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )
        )}
      </div>

      {/* Create partner modal */}
      {showCreate && (
        <div className="mp-overlay" onClick={() => setShowCreate(false)}>
          <div className="mp-modal" onClick={e => e.stopPropagation()}>
            <div className="mp-modal-header">
              <h3>{t('PartnersPage.modalTitle')}</h3>
              <button onClick={() => setShowCreate(false)}><i className="fas fa-times" /></button>
            </div>
            <div className="mp-modal-body">
              <div className="mp-field">
                <label>{t('PartnersPage.labelPartnerType')}</label>
                <select className="mp-input" value={form.partnerType}
                  onChange={e => setForm(f => ({ ...f, partnerType: e.target.value }))}>
                  <option value="CISO_RESELLER">{t('PartnersPage.partnerTypeCisoReseller')}</option>
                  <option value="CPA_FIRM">{t('PartnersPage.partnerTypeCpaFirm')}</option>
                </select>
              </div>
              {[[t('PartnersPage.labelFirmName'), 'text', 'firmName', t('PartnersPage.placeholderFirmName')],
                [t('PartnersPage.labelContactEmail'), 'email', 'contactEmail', t('PartnersPage.placeholderContactEmail')],
                [t('PartnersPage.labelWebsiteUrl'), 'text', 'websiteUrl', t('PartnersPage.placeholderWebsiteUrl')],
                [t('PartnersPage.labelCommissionRate'), 'number', 'commissionRate', t('PartnersPage.placeholderCommissionRate')],
              ].map(([label, type, key, placeholder]) => (
                <div key={key} className="mp-field">
                  <label>{label}</label>
                  <input type={type} className="mp-input" value={form[key]} placeholder={placeholder}
                    onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} />
                </div>
              ))}
              {msg && <div className="mp-error">{msg}</div>}
            </div>
            <div className="mp-modal-footer">
              <button className="mp-btn-secondary" onClick={() => setShowCreate(false)}>{t('PartnersPage.cancel')}</button>
              <button className="mp-btn-primary" onClick={handleCreate} disabled={creating}>
                {creating ? t('PartnersPage.creating') : <><i className="fas fa-plus" /> {t('PartnersPage.createPartner')}</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
